import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * Field composition primitives for Input / Textarea — from the Figma DS2026
 * "Input field" + "Text area" specs (First Bite Design System, nodes
 * 1536:18108 / 7148:6098).
 *
 *   <Field status="error">
 *     <FieldLabel htmlFor="email">Email</FieldLabel>
 *     <Input id="email" placeholder="info@gmail.com" />
 *     <FieldHint>This is a hint text to help user.</FieldHint>
 *   </Field>
 *
 * `Field` publishes its `status` through context so the enclosed `Input` /
 * `Textarea` border + ring and the `FieldHint` text recolor together
 * (default → gray, error → red, success → green) — matching the three Figma
 * columns. A `status` prop set directly on `Input`/`Textarea` always wins over
 * the context, so the controls also work standalone (without a `Field`).
 */

export type FieldStatus = 'default' | 'error' | 'success'

const FieldStatusContext = React.createContext<FieldStatus | undefined>(
  undefined
)

/** Resolve the effective status: explicit prop > Field context > 'default'. */
export function useFieldStatus(status?: FieldStatus): FieldStatus {
  const ctx = React.useContext(FieldStatusContext)
  return status ?? ctx ?? 'default'
}

/**
 * Resting + focus border and the translucent 4px focus ring, per status — the
 * Figma "Brand/Error/Success focus border color" + "Focus Rings/*" tokens.
 * Shared by `inputVariants` and `textareaVariants` so the controls stay in step.
 */
export const fieldControlStatus = {
  default:
    'border-fbui-gray-300 focus:border-fbui-basil-700 focus:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus:border-fbui-red-500 focus:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus:border-fbui-green-500 focus:ring-fbui-ring-success',
} as const satisfies Record<FieldStatus, string>

/**
 * The same per-status border + ring, but keyed off `focus-within` — for shells
 * that wrap a borderless control plus addons (e.g. `InputGroup` / `SearchBar`),
 * so the whole row lights up when the inner input is focused.
 */
export const fieldGroupStatus = {
  default:
    'border-fbui-gray-300 focus-within:border-fbui-basil-700 focus-within:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus-within:border-fbui-red-500 focus-within:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus-within:border-fbui-green-500 focus-within:ring-fbui-ring-success',
} as const satisfies Record<FieldStatus, string>

/** Help-text color, per status (gray / red / green) — the Figma hint rows. */
export const fieldHintColor = {
  default: 'text-fbui-gray-500',
  error: 'text-fbui-red-600',
  success: 'text-fbui-green-600',
} as const satisfies Record<FieldStatus, string>

export interface FieldProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Status shared with the enclosed control + hint. Defaults to 'default'. */
  status?: FieldStatus
}

/** Vertical stack (label · control · hint) that publishes `status` to children. */
export const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  ({ className, status = 'default', children, ...props }, ref) => (
    <FieldStatusContext.Provider value={status}>
      <div
        ref={ref}
        className={cn('flex w-full flex-col gap-1.5', className)}
        {...props}
      >
        {children}
      </div>
    </FieldStatusContext.Provider>
  )
)
Field.displayName = 'Field'

export interface FieldLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  /** Append a muted "(optional)" suffix after the label text. */
  optional?: boolean
}

/** Form label — 14px medium gray-700, with an optional muted "(optional)". */
export const FieldLabel = React.forwardRef<HTMLLabelElement, FieldLabelProps>(
  ({ className, optional, children, ...props }, ref) => (
    <label
      ref={ref}
      className={cn(
        'text-sm font-medium tracking-[-0.2px] text-fbui-gray-700 select-none',
        className
      )}
      {...props}
    >
      {children}
      {optional && (
        <span className="ml-1 font-normal text-fbui-gray-500">(optional)</span>
      )}
    </label>
  )
)
FieldLabel.displayName = 'FieldLabel'

export interface FieldHintProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /** Override the inherited Field status (e.g. for a standalone hint). */
  status?: FieldStatus
}

/** Help text under the control — 12px, recolored to match the field status. */
export const FieldHint = React.forwardRef<HTMLParagraphElement, FieldHintProps>(
  ({ className, status, children, ...props }, ref) => {
    const resolved = useFieldStatus(status)
    if (!children) return null
    return (
      <p
        ref={ref}
        className={cn(
          'text-xs tracking-[-0.2px]',
          fieldHintColor[resolved],
          className
        )}
        {...props}
      >
        {children}
      </p>
    )
  }
)
FieldHint.displayName = 'FieldHint'
